import React, { PureComponent } from 'react';
import telemetry from 'legacy-src/utility/telemetry';
import {
  openSecureBrowserLink,
  SECURE_BROWSER_STATUS_CHECKING,
  SECURE_BROWSER_STATUS_FOUND,
  SECURE_BROWSER_STATUS_MISSING,
} from 'legacy/src/components/apstudent/handlers/Assignments/lockdownbrowser';
import TimedAssignmentWarningModal from 'chameleon/ui-stack/ui/assignments/timed-assignment-warning-modal';
import {
  timeLeftUntilCbSessionTimeout,
  timeLeftUntilCBSessionExpire,
} from 'legacy/src/components/auth/utils';
import { cleanReturnPath } from 'chameleon/src/components/TodoCards/helpers';

const GET_FEEDBACK = 'GET FEEDBACK';
const SCORE = 'SCORE';

export const MOBILE_LDB_MESSAGE =
  'Secure assignments cannot be taken on a phone. ' +
  'To complete this assignment, please use a laptop or tablet.';

/**
 * @classdesc Parent component of
 * StudentIndex and .
 * client/src/components/apstudent/handlers/Assignments/index.js
 * to facilitate sharing logic for opening assignments
 */
class AssignmentOpen extends PureComponent {
  static displayName = 'AssignmentOpen';

  state = {
    showSessionExpirationModal: false,
    showAssignmentModal: false,
  };

  onSecureAssessmentOnMobileConfirm = () => {
    this.setState({ secureAssessmentOnMobile: false });
  };

  onBrowserStatusChange = (status) => {
    switch (status) {
      case SECURE_BROWSER_STATUS_FOUND:
        break;
      case SECURE_BROWSER_STATUS_CHECKING:
        break;
      case SECURE_BROWSER_STATUS_MISSING:
        this.setState({ showMissingSecureBrowserBanner: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.missingSecureBrowserTimeoutId = setTimeout(() => {
          this.setState({ showMissingSecureBrowserBanner: false });
        }, 180000);
        break;
      default:
        break;
    }
  };

  /**
   * Click handler for the Begin button for a timed assignment
   * @param {Object} assignment The assignment object
   * @param {string} returnPath
   */
  onTimedAssessmentClick = (assignment, returnPath = '') => {
    const {
      checkCBSessionFlag,
      cbSessionExpirationOverride,
      levityUseCreateTime,
      showTimedAssignmentWarning,
    } = this.props;
    const useCreateTimeOrExpireTime = levityUseCreateTime
      ? timeLeftUntilCbSessionTimeout()
      : timeLeftUntilCBSessionExpire();
    // hard override only used for debugging, should remain off, see FY-12447
    const expirationTimeDiff =
      cbSessionExpirationOverride || useCreateTimeOrExpireTime;
    const { timer } = assignment;
    const expiringSession = expirationTimeDiff <= timer;

    if (checkCBSessionFlag && showTimedAssignmentWarning && expiringSession) {
      this.beginAssignment(assignment, returnPath);
      // if user has no expiration time, render the regular timed assessment warning
      // if no expiration time, we assume it's a direct login and render the regular timed assessment warning
      // if expire time greater than assignment timer, render the regular timed assessment warning
    } else if (checkCBSessionFlag && showTimedAssignmentWarning) {
      this.setState({
        showSessionExpirationModal: false,
        showAssignmentModal: true,
        assignment,
        returnPath,
        timer: assignment.timer,
      });
    } else {
      this.beginAssignment(assignment, returnPath);
    }
  };

  /**
   * Get the assignment URL by its id
   * @param {object} assignment
   * @returns {String}
   **/
  getAssignmentUrl = ({
    id,
    is_secure,
    secure_link,
    link,
    headline,
    is_spp,
  }) => {
    const { params } = this.props;
    return is_secure && headline !== GET_FEEDBACK && headline !== SCORE
      ? secure_link
      : is_spp
        ? link
        : id
          ? `/${params.subject}/assessments/assignments/${id}`
          : `/${link}`;
  };

  secureAssessmentOnMobileModalProps = () => ({
    content: <p>{MOBILE_LDB_MESSAGE}</p>,
    title: 'Secure Assessment Not Supported',
    onClose: this.onSecureAssessmentOnMobileConfirm,
  });

  /**
   * Called when any assignment button has been clicked
   * @param {Number/String} assignmentId
   * @param {Object} assignment
   * @returns {String}
   **/
  @telemetry((props, state, args) => {
    const [assignment_id, assignment, title] = args;
    const { progress = {} } = assignment || {};
    const event_type = progress.started
      ? 'continue_assessment'
      : 'begin_assessment';

    return {
      event_type,
      params: {
        element: title ? 'title' : 'button',
        assignment_id,
      },
    };
  })

  // eslint-disable-next-line class-methods-use-this
  triggerDecorator() {
    // Return true to get the telemetry decorator triggered
    return true;
  }

  /**
   * Navigates to a given assignment by its id
   * @param {Object} assignment
   * @param {string} returnPath
   **/
  beginAssignment = (assignment, returnPath = '', focusedElement = '') => {
    const { router, isLdbEnabled, isMobileDevice } = this.props;
    const url = this.getAssignmentUrl(assignment);
    const { headline, is_secure } = assignment;
    const { location } = router || {};
    returnPath = `${location.pathname}${location.search}`;

    const cleanedReturnPath = cleanReturnPath(returnPath);
    if (isLdbEnabled && is_secure && headline !== GET_FEEDBACK) {
      // PREAP-1483: if we require a secure browser url, the url provided is expected to be an ldb1 protocol. We'll need to check
      // that this host actually has it installed.
      if (isMobileDevice) {
        this.setState({ secureAssessmentOnMobile: true });
      } else {
        this.triggerDecorator();
        openSecureBrowserLink(url, this.onBrowserStatusChange);
      }
    } else {
      this.triggerDecorator();
      if (focusedElement) {
        router.push({
          pathname: url,
          state: {
            returnPath: cleanedReturnPath,
            focusedElement,
          },
        });
      } else {
        router.push({
          pathname: url,
          state: {
            returnPath: cleanedReturnPath,
          },
        });
      }
    }
  };

  onModalSuccess = () => {
    const { assignment, returnPath } = this.state;
    this.beginAssignment(assignment, returnPath);
  };

  onModalClose = () => {
    this.setState({
      showAssignmentModal: false,
      showSessionExpirationModal: false,
    });
  };

  renderModals() {
    const { showAssignmentModal, timer } = this.state;

    if (showAssignmentModal) {
      return (
        <TimedAssignmentWarningModal
          onSuccess={this.onModalSuccess}
          onClose={this.onModalClose}
          timer={timer}
        />
      );
    }

    return null;
  }
}

export default AssignmentOpen;
